import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../../services/api.service';
import GalleryGrid from '../../../components/GalleryGrid';
import './GalleryList.css';

const GalleryList = () => {
  const navigate = useNavigate();
  const [galleries, setGalleries] = useState([]);
  const [filteredGalleries, setFilteredGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchGalleries();
  }, []);

  useEffect(() => {
    filterGalleries();
  }, [galleries, searchTerm, statusFilter]);

  const fetchGalleries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAdminGalleries();
      if (response.data.success) {
        setGalleries(response.data.data);
      } else {
        setError('Failed to load galleries');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load galleries');
      console.error('Galleries fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterGalleries = () => {
    let filtered = [...galleries];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(gallery => 
        gallery.category_title?.toLowerCase().includes(searchLower) ||
        gallery.category_location?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(gallery => gallery.display_status === statusFilter);
    }

    setFilteredGalleries(filtered);
  };

  const handleDelete = async (galleryId) => {
    try {
      setError(null);
      const response = await apiService.deleteGallery(galleryId);
      if (response.data.success) {
        setSuccessMessage('Gallery deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        fetchGalleries(); // Refresh the list
      } else {
        setError('Failed to delete gallery');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete gallery');
      console.error('Gallery delete error:', err);
    }
  };

  const handleEdit = (galleryId) => {
    navigate(`/admin/galleries/edit/${galleryId}`);
  };

  const handleViewPhotos = (galleryId) => {
    navigate(`/admin/galleries/${galleryId}/photos`);
  };

  const handleAddNew = () => {
    navigate('/admin/galleries/new');
  };

  return (
    <div className="gallery-list-container">
      <div className="gallery-list-header">
        <h2>Gallery Management</h2>
        <button className="btn btn-primary" onClick={handleAddNew}>
          + Add New Gallery
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <strong>Success:</strong> {successMessage}
        </div>
      )}

      <div className="gallery-list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search galleries by title or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-box">
          <label htmlFor="statusFilter">Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
      </div>

      <div className="gallery-list-stats">
        <span>
          Showing {filteredGalleries.length} of {galleries.length} galleries
          {searchTerm || statusFilter !== 'all' ? ' (filtered)' : ''}
        </span>
      </div>

      <GalleryGrid
        galleries={filteredGalleries}
        onDelete={handleDelete}
        onEdit={handleEdit}
        onViewPhotos={handleViewPhotos}
        loading={loading}
      />
    </div>
  );
};

export default GalleryList;

